// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartStyleFactories.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------
import Factories from './Izenda.Factories';
import { includes, isNil, isEmpty } from 'lodash/fp';

const ciFactories = new Factories('Report Part Style');
const composeKey = (reportPartType, reportPartStyle) => `${reportPartStyle}-${reportPartType}`;

export default {
  /**
   * Add chart info
   */
  add(reportPartType: string, reportPartStyle: string, baseStyle: string, label: string, previewCSS: string): void {
    ciFactories.register(composeKey(reportPartType, reportPartStyle), () => ({
      type: reportPartType,
      style: reportPartStyle,
      baseStyle,
      label,
      previewCSS
    }));
  },

  /**
   * Get all chart infos
   */
  getAll(reportPartType: string) {
    return ciFactories
      .values()
      .map(fn => fn())
      .filter(({ type }) => type === reportPartType);
  },

  /**
   * Get base style of a report part style
   */
  getBaseStyle(reportPartType) {
    ensure(reportPartType, 'reportPartType must have a value');

    return reportPartStyle => {
      ensure(reportPartStyle, 'reportPartStyle must have a value');

      const key = composeKey(reportPartType, reportPartStyle);

      const { baseStyle } = ciFactories.containKey(key) ? ciFactories.create(key) : {};
      return baseStyle;
    };
  },

  /**
   * Determine whether a report part style is compatible with any base styles
   * @param {string} reportPartType The report part type
   * @param {string} reportPartStyle The report part style
   * @param {Array<string>} baseStyles The array of base report part styles to compatible with
   */
  isReportPartStyle: function isReportPartStyle(reportPartType) {
    const getBase = this.getBaseStyle(reportPartType);

    return (reportPartStyle, ...baseStyles) => {
      ensure(reportPartType, 'reportPartType must have a value');
      ensure(reportPartStyle, 'reportPartStyle must have a value');
      ensure(baseStyles && baseStyles.length > 0, 'baseStyles must have a value');

      if (includes(reportPartStyle, baseStyles)) {
        return true;
      }

      const next = getBase(reportPartStyle);
      if (isNil(next) || isEmpty(next)) {
        return false;
      }

      return this.isReportPartStyle(reportPartType)(next, ...baseStyles);
    };
  },

  /**
   * Reset the factories data
   */
  reset() {
    ciFactories.reset();
  }
};



// WEBPACK FOOTER //
// ./ui/share/factories/Izenda.ReportPartStyleFactories.js